import {
  bandDaysRemaining,
  SNAPSHOT_DETAIL_RANGES,
  type SnapshotDetailRange,
  type SnapshotTrend,
  smoothSeries,
  snapshotVerdictPhrase,
} from "@healthos/shared";
import { useCallback, useEffect, useMemo, useRef } from "react";
import uPlot from "uplot";

/*
 * The band holds a stated share of the plot, so a line that stays inside it
 * cannot turn the band into the whole chart.
 */
const BAND_PAD = 0.14;

/** Room to the right of the plot for the y axis, in CSS pixels. */
const Y_GUTTER = 58;

/** The strip under the plot the context marks live in. */
/** Keeps the gap label clear of the plot's left edge. */
const GAP_LABEL_INSET = 8;
/** The dot that puts a context mark on the mean line. */
const MARK_DOT_R = 3;
/** Room the "usual range forming" note takes, so the gap note doesn't sit on top of it. */
const BAND_FORMING_LABEL_H = 14;
/** How long the mean takes to reach full weight, in days. */
const FADE_DAYS = 4;
const LANE_H = 16;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Canvas cannot read a custom property, so the five chart colours are declared
// on .snap-chart-box and resolved here at draw time. The palette stays in CSS.
/** Canvas normalises any colour it accepts to hex, which is parseable. */
function withAlpha(ctx: CanvasRenderingContext2D, colour: string, alpha: number): string {
  const before = ctx.fillStyle;
  ctx.fillStyle = colour;
  const hex = String(ctx.fillStyle);
  ctx.fillStyle = before;
  if (!hex.startsWith("#") || hex.length < 7) return hex;
  const to = (i: number) => Number.parseInt(hex.slice(i, i + 2), 16);
  return `rgba(${to(1)}, ${to(3)}, ${to(5)}, ${alpha})`;
}

/**
 * The mean emerges and recedes rather than being cut.
 *
 * A centred seven-day mean is only worth its name where its window is full, so
 * the line carries the weight its window has. It comes up out of nothing where
 * the record starts, goes back to nothing either side of a stretch the member
 * did not report, and stays solid through the days that did. The one end that
 * does not fade is the newest reading, because the dot anchors it and that is
 * the point the member came to read.
 *
 * Drawn as one stroke gradient across the plot rather than a mask, so the bars
 * and the band underneath are untouched.
 */
function meanStroke(
  u: uPlot,
  colour: string,
  smooth: ReadonlyArray<number | null>,
): string | CanvasGradient {
  const runs: Array<[number, number]> = [];
  let open = -1;
  for (let i = 0; i < smooth.length; i++) {
    const has = smooth[i] != null;
    if (has && open < 0) open = i;
    if (!has && open >= 0) {
      runs.push([open, i - 1]);
      open = -1;
    }
  }
  if (open >= 0) runs.push([open, smooth.length - 1]);
  const first = runs[0];
  const last = runs[runs.length - 1];
  if (!first || !last) return colour;
  // One run that already starts at the plot's edge has nothing to emerge from.
  if (runs.length === 1 && first[0] === 0) return colour;

  const left = u.bbox.left;
  const span = u.bbox.width || 1;
  const at = (i: number) => Math.min(1, Math.max(0, (u.valToPos(i, "x", true) - left) / span));
  const gradient = u.ctx.createLinearGradient(left, 0, left + span, 0);
  const clear = withAlpha(u.ctx, colour, 0);

  let prev = -1;
  const stop = (offset: number, tone: string) => {
    const next = Math.min(1, Math.max(offset, prev + 1e-6));
    gradient.addColorStop(next, tone);
    prev = next;
  };
  for (const [from, to] of runs) {
    if (from === 0) stop(at(from), colour);
    else {
      stop(at(from), clear);
      stop(at(Math.min(from + FADE_DAYS, to)), colour);
    }
    if (to === last[1]) stop(at(to), colour);
    else {
      stop(at(Math.max(to - FADE_DAYS, from)), colour);
      stop(at(to), clear);
    }
  }
  return gradient;
}

function chartInk(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const v = (name: string) => (cs.getPropertyValue(name) || "").trim() || "transparent";
  return {
    line: v("--snap-line"),
    band: v("--snap-band"),
    bandEdge: v("--snap-band-edge"),
    med: v("--snap-med"),
    plotBg: v("--snap-plot-bg"),
    gap: v("--snap-gap-bg"),
    dot: v("--snap-dot"),
    barIn: v("--snap-bar-in"),
    barOut: v("--snap-bar-out"),
    stripe: v("--snap-plot-stripe"),
    target: v("--snap-target"),
  };
}

/*
 * The labels drawn over the canvas. Each is placed the same way, from uPlot's
 * own value-to-pixel conversion, so they are declared once here and written
 * through one `place` rather than nine near-identical blocks.
 */
const LAB_CLASS = {
  mark: "snap-chart-mark",
  band: "snap-band-lab",
  target: "snap-target-lab",
  gap: "snap-gap-lab",
  yHi: "snap-yl",
  yLo: "snap-yl",
  xFirst: "snap-xl",
  xMid: "snap-xl",
  xLast: "snap-xl",
} as const;
type Lab = keyof typeof LAB_CLASS;

/** A label mid-sentence: lower-cased, unless it is an acronym like HRV. */
function midSentence(label: string): string {
  return /^[A-Z]{2,}/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);
}

export function TrendChart({
  trend,
  days,
  onDays,
}: {
  trend: SnapshotTrend;
  days: SnapshotDetailRange;
  onDays: (next: SnapshotDetailRange) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const labs = useRef<Partial<Record<Lab, HTMLSpanElement | null>>>({});
  const readValRef = useRef<HTMLElement>(null);
  const readWhenRef = useRef<HTMLElement>(null);
  const readMarkRef = useRef<HTMLElement>(null);

  const band = trend.band;
  const dp = trend.dp;
  const fmt = useMemo(
    () => (v: number) =>
      v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }),
    [dp],
  );
  const data = useMemo(() => trend.series.slice(-days), [trend.series, days]);
  const verdicts = useMemo(() => trend.verdicts.slice(-days), [trend.verdicts, days]);
  const smooth = useMemo(() => smoothSeries(data), [data]);
  // Context is indexed against the whole series; the chart draws a slice of it.
  const marks = useMemo(() => {
    const start = trend.series.length - data.length;
    return trend.context.filter((c) => c.day >= start).map((c) => ({ ...c, at: c.day - start }));
  }, [trend.context, trend.series.length, data.length]);
  const marksByDay = useMemo(() => {
    const m = new Map<number, string[]>();
    for (const c of marks) m.set(c.at, [...(m.get(c.at) ?? []), c.label]);
    return m;
  }, [marks]);

  const target = trend.target;
  const reported = useMemo(() => data.filter((v) => v !== null).length, [data]);
  const firstReading = useMemo(() => data.findIndex((v) => v !== null), [data]);
  /** Runs of days with no reading, after the first one, as [from, to] pairs. */
  const gaps = useMemo(() => {
    const out: Array<[number, number]> = [];
    if (firstReading < 0) return out;
    let run = -1;
    for (let i = firstReading; i < data.length; i++) {
      if (data[i] === null) {
        if (run < 0) run = i;
      } else if (run >= 0) {
        out.push([run, i - 1]);
        run = -1;
      }
    }
    if (run >= 0) out.push([run, data.length - 1]);
    return out;
  }, [data, firstReading]);

  const uData = useMemo(() => {
    const xs = data.map((_, i) => i);
    // uPlot strokes one series one way, so the two verdicts are two series: a
    // day inside the member's own range and a day outside it carry the same
    // colours here as the rows in the table below, and agree by eye.
    const out = (i: number) => verdicts[i] === "under" || verdicts[i] === "over";
    const inBand = data.map((v, i) => (v !== null && out(i) ? null : v));
    const outBand = data.map((v, i) => (v !== null && out(i) ? v : null));
    return [
      xs,
      xs.map(() => band?.lo ?? null),
      xs.map(() => band?.hi ?? null),
      xs.map(() => band?.med ?? null),
      xs.map(() => target?.value ?? null),
      inBand,
      outBand,
      smooth,
    ] as unknown as uPlot.AlignedData;
  }, [data, smooth, band, target, verdicts]);

  // The domain holds every night drawn, the band, and the target, so a member
  // below their target can see how far below it they are.
  const yRange = useMemo<[number, number]>(() => {
    const anchors = data.filter((v): v is number => v !== null);
    if (band) anchors.push(band.lo, band.hi);
    if (target) anchors.push(target.value);
    if (anchors.length === 0) return [0, 1];
    const height = band
      ? Math.max(band.hi - band.lo, 1)
      : Math.max(...anchors) - Math.min(...anchors) || 1;
    const lo = Math.min(...anchors) - height * BAND_PAD;
    const hi = Math.max(...anchors) + height * BAND_PAD;
    const span = hi - lo || 1;
    return [lo - span * 0.03, hi + span * 0.03];
  }, [band, data, target]);

  // At rest the chart states its most recent reading, which is not always
  // today: a day nothing reported has no mean to state.
  const latest = useMemo(() => {
    for (let i = data.length - 1; i >= 0; i--) if (data[i] !== null) return i;
    return null;
  }, [data]);
  const writeReading = useCallback(
    (at: number | null) => {
      const i = at ?? latest;
      const v = i === null ? null : (smooth[i] ?? null);
      const val = readValRef.current;
      const when = readWhenRef.current;
      const mark = readMarkRef.current;
      if (!val || !when) return;
      if (mark) {
        mark.textContent = "";
        mark.hidden = true;
      }
      if (i === null || v === null) {
        val.textContent = "";
        when.textContent = "Nothing reported";
        return;
      }
      const ago = smooth.length - 1 - i;
      val.textContent = `${v.toFixed(dp)}${trend.unit.trim()}`;
      const day = ago === 0 ? "today" : ago === 1 ? "yesterday" : `${ago} days ago`;
      // The number is the 7-day mean, and the dots under the cursor are the
      // days themselves, so the readout says which one it is quoting. When
      // something else was unusual that day, it says that too: alongside, which
      // is all the chart can honestly claim.
      const also = at === null ? undefined : marksByDay.get(at);
      // The bar is the day; the line is the week. Hovering a bar that asked a
      // question ("why is that one red") has to answer with that day's own
      // reading and where it sat, not only with the mean running through it.
      const raw = data[i] ?? null;
      const parts = [`7-day mean, ${day}`];
      if (raw !== null) {
        const sat = snapshotVerdictPhrase(verdicts[i] ?? null);
        parts.push(sat === null ? fmt(raw) : `${fmt(raw)} that day, ${sat}`);
      }
      when.textContent = parts.join(" · ");
      // What else was true that day reads as its own chip, so it is findable
      // rather than trailing the sentence.
      if (mark && also?.length) {
        mark.textContent = also.join(" · ");
        mark.hidden = false;
      }
    },
    [smooth, latest, dp, trend.unit, verdicts, marksByDay, data, fmt],
  );
  useEffect(() => {
    writeReading(null);
  }, [writeReading]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const ink = chartInk(host);
    const linear = uPlot.paths.linear?.();
    const spline = uPlot.paths.spline?.();
    const bars = uPlot.paths.bars?.({ size: [0.55, 22], align: 0 });

    const opts: uPlot.Options = {
      width: host.clientWidth || 300,
      height: host.clientHeight || 200,
      legend: { show: false },
      cursor: { y: false, drag: { x: false, y: false, setScale: false }, points: { show: false } },
      axes: [
        {
          size: 26,
          gap: 0,
          grid: { show: false },
          ticks: { show: false },
          splits: () => [],
          values: () => [],
        },
        { show: false },
      ],
      // A right gutter for the y axis, so no value is written over the plot.
      padding: [10, Y_GUTTER, LANE_H, 8],
      scales: { x: { time: false }, y: { range: () => yRange } },
      series: [
        {},
        { stroke: ink.bandEdge, width: 1, points: { show: false }, paths: linear },
        { stroke: ink.bandEdge, width: 1, points: { show: false }, paths: linear },
        { stroke: "transparent", points: { show: false }, paths: linear },
        { stroke: ink.target, width: 1, dash: [2, 4], points: { show: false }, paths: linear },
        // The days themselves, as columns from the axis, so the curve is never
        // mistaken for a reading the member actually had.
        {
          stroke: "transparent",
          width: 0,
          fill: ink.barIn,
          points: { show: false },
          paths: bars,
        },
        {
          stroke: "transparent",
          width: 0,
          fill: ink.barOut,
          points: { show: false },
          paths: bars,
        },
        // The 7-day mean is a thin read over the columns, never a column itself.
        {
          stroke: (u) => meanStroke(u, ink.line, smooth),
          width: 1.5,
          points: { show: false },
          paths: spline,
        },
      ],
      bands: [{ series: [2, 1], fill: ink.band }],
      hooks: {
        drawClear: [
          (u) => {
            u.ctx.save();
            u.ctx.fillStyle = ink.plotBg;
            u.ctx.fillRect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
            u.ctx.fillStyle = ink.stripe;
            // Read the day count off the plot rather than the closure, so the
            // stripes cannot fall out of step with what is drawn.
            const n = u.data[0].length;
            for (let i = 0; i < n; i++) {
              const dow = new Date(Date.now() - (n - 1 - i) * 86_400_000).getDay();
              if (dow !== 0 && dow !== 6) continue;
              const x0 = Math.max(u.valToPos(i - 0.5, "x", true), u.bbox.left);
              const x1 = Math.min(u.valToPos(i + 0.5, "x", true), u.bbox.left + u.bbox.width);
              if (x1 > x0) u.ctx.fillRect(x0, u.bbox.top, x1 - x0, u.bbox.height);
            }
            // Days before the member's first reading are not a flat week, they
            // are days we have nothing for. Shading them says which is which.
            u.ctx.fillStyle = ink.gap;
            if (firstReading > 0) {
              const to = u.valToPos(firstReading, "x", true);
              u.ctx.fillRect(u.bbox.left, u.bbox.top, to - u.bbox.left, u.bbox.height);
            }
            // Interior stretches with nothing reported read as a snapped line
            // otherwise, which looks like a fault rather than an absence.
            for (const [from, to] of gaps) {
              const x0 = u.valToPos(from - 0.5, "x", true);
              const x1 = u.valToPos(to + 0.5, "x", true);
              u.ctx.fillRect(x0, u.bbox.top, x1 - x0, u.bbox.height);
            }
            u.ctx.restore();
          },
        ],
        // The readout follows the cursor by ref, the way every other label on
        // this canvas does, so a mouse sweep does not re-render the figure.
        setCursor: [(u) => writeReading(u.cursor.idx ?? null)],
        draw: [
          (u) => {
            const dpr = devicePixelRatio;
            /** Position a label, or hide it when it has nothing to say. */
            const place = (k: Lab, text: string | null, x: number, y: number, anchor = "") => {
              const el = labs.current[k];
              if (!el) return;
              el.style.opacity = text === null ? "0" : "1";
              if (text === null) {
                // Cleared as well as hidden: the same element is reused across
                // tabs, and a hidden label still holds the last tab's text.
                el.textContent = "";
                return;
              }
              el.textContent = text;
              el.style.transform = `translate(${x}px, ${y}px) ${anchor}`;
            };
            const line = u.data[u.data.length - 1] as ReadonlyArray<number | null>;
            let last = u.data[0].length - 1;
            while (last > 0 && line[last] === null) last--;
            const lineValue = line[last];
            place(
              "mark",
              lineValue === null || lineValue === undefined ? null : "",
              u.valToPos(u.data[0][last] as number, "x", true) / dpr,
              u.valToPos(lineValue ?? 0, "y", true) / dpr,
              "translate(-50%, -50%)",
            );
            const left = u.bbox.left / dpr;
            const right = (u.bbox.left + u.bbox.width) / dpr;
            const bottom = (u.bbox.top + u.bbox.height) / dpr;
            const top = u.bbox.top / dpr;
            // Every value sits in the gutter, on the line it names, so the
            // plot carries data and nothing else.
            const gutter = right + 8;
            if (band) {
              const hiY = u.valToPos(band.hi, "y", true) / dpr;
              const loY = u.valToPos(band.lo, "y", true) / dpr;
              place("yHi", fmt(band.hi), gutter, hiY, "translateY(-50%)");
              place("yLo", fmt(band.lo), gutter, loY, "translateY(-50%)");
              place("band", null, 0, 0);
            } else {
              // Without a band the domain's own ends are the only scale.
              const [lo, hi] = yRange;
              place("yHi", fmt(hi), gutter, top, "translateY(-50%)");
              place("yLo", fmt(lo), gutter, bottom, "translateY(-50%)");
              const days = bandDaysRemaining(trend.series);
              place(
                "band",
                days > 0
                  ? `usual range forming · ${days} more ${days === 1 ? "day" : "days"}`
                  : "usual range forming",
                left + 8,
                top + 4,
              );
            }
            place(
              "target",
              target ? target.label : null,
              gutter,
              target ? u.valToPos(target.value, "y", true) / dpr : 0,
              "translateY(-50%)",
            );
            // The lane: one tick per day something else sat outside its usual,
            // with a hairline up through the plot, so the day a tick belongs to
            // is findable rather than guessed at from the tick alone.
            // The lane tick says a day had something else going on; the dot on
            // the mean says where that day sits on the line, and the dashes
            // join the two, so the mark is a thing to point at rather than a
            // stripe to notice.
            u.ctx.save();
            for (const m of marks) {
              const x = u.valToPos(m.at, "x", true);
              const tone = m.kind === "calendar" ? ink.line : ink.target;
              const y = smooth[m.at] == null ? null : u.valToPos(smooth[m.at] as number, "y", true);
              u.ctx.strokeStyle = tone;
              u.ctx.globalAlpha = 0.35;
              u.ctx.setLineDash([2 * dpr, 3 * dpr]);
              u.ctx.lineWidth = dpr;
              u.ctx.beginPath();
              u.ctx.moveTo(x, y ?? u.bbox.top);
              u.ctx.lineTo(x, u.bbox.top + u.bbox.height);
              u.ctx.stroke();
              u.ctx.setLineDash([]);
              u.ctx.globalAlpha = 1;
              if (y !== null) {
                u.ctx.fillStyle = ink.plotBg;
                u.ctx.beginPath();
                u.ctx.arc(x, y, MARK_DOT_R * dpr, 0, Math.PI * 2);
                u.ctx.fill();
                u.ctx.strokeStyle = tone;
                u.ctx.lineWidth = 1.5 * dpr;
                u.ctx.stroke();
              }
              u.ctx.fillStyle = tone;
              u.ctx.fillRect(x - 2 * dpr, u.bbox.top + u.bbox.height + 4 * dpr, 4 * dpr, 8 * dpr);
            }
            u.ctx.restore();

            const end = u.data[0].length - 1;
            const dayLabel = (i: number) => {
              const d = new Date(Date.now() - (end - i) * 86_400_000);
              return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
            };
            const mid = Math.round(end / 2);
            place("xFirst", dayLabel(0), left, bottom + LANE_H + 4);
            place(
              "xMid",
              dayLabel(mid),
              u.valToPos(u.data[0][mid] as number, "x", true) / dpr,
              bottom + LANE_H + 4,
              "translateX(-50%)",
            );
            place("xLast", "Today", right, bottom + LANE_H + 4, "translateX(-100%)");
            const gapTo = firstReading > 0 ? u.valToPos(firstReading, "x", true) / dpr : 0;
            // Sits at the top of the empty stretch it names, not centred in
            // the plot: mid-height it read as a note about the chart rather
            // than a caption on the days that have nothing.
            place(
              "gap",
              firstReading > 0 ? `no readings before ${dayLabel(firstReading)}` : null,
              Math.min(left + GAP_LABEL_INSET, (left + gapTo) / 2),
              top + GAP_LABEL_INSET + (band ? 0 : BAND_FORMING_LABEL_H),
            );
          },
        ],
      },
    };

    const plot = new uPlot(opts, uData, host);
    const observer = new ResizeObserver(() => {
      if (host.clientWidth > 0) {
        plot.setSize({ width: host.clientWidth, height: host.clientHeight });
      }
    });
    observer.observe(host);
    return () => {
      observer.disconnect();
      plot.destroy();
    };
  }, [
    uData,
    yRange,
    band,
    fmt,
    trend.series,
    target,
    firstReading,
    gaps,
    marks,
    writeReading,
    smooth,
  ]);

  // Nothing reported is not a chart. Drawing the frame anyway invented a 0 to 1
  // axis and captioned it as a range still forming, which reads as data on its
  // way rather than a source that was never connected.
  if (reported === 0) {
    return (
      <figure className="snap-chart snap-chart-none">
        <div className="snap-section-head snap-chart-head">
          <h3>{trend.label}</h3>
        </div>
        <p className="snap-chart-empty">
          Nothing has reported {trend.label.toLowerCase()} yet. Connect a source that measures it
          and this fills in.
        </p>
      </figure>
    );
  }

  return (
    <figure className="snap-chart">
      <div className="snap-section-head snap-chart-head">
        <h3>{trend.label}</h3>
        <span className="snap-chart-read">
          <b ref={readValRef} />
          <i ref={readWhenRef} />
          <em className="snap-chart-chip" ref={readMarkRef} />
        </span>
        <fieldset className="snap-chart-range">
          <legend className="sr-only">Chart range</legend>
          {SNAPSHOT_DETAIL_RANGES.map((r) => (
            <button key={r} type="button" aria-pressed={days === r} onClick={() => onDays(r)}>
              {r}d
            </button>
          ))}
        </fieldset>
      </div>
      <div className="snap-chart-box">
        <div className="snap-uplot" ref={hostRef} />
        {(Object.keys(LAB_CLASS) as Lab[]).map((k) => (
          <span
            key={k}
            className={LAB_CLASS[k]}
            aria-hidden="true"
            ref={(el) => {
              labs.current[k] = el;
            }}
          />
        ))}
      </div>
      <figcaption className="snap-chart-foot">
        <ul className="snap-chart-key">
          <li>
            <i className="snap-key-line" aria-hidden="true" />
            7-day mean
          </li>
          <li>
            <i className="snap-key-bar" aria-hidden="true" />
            your days
          </li>
          {band ? (
            <li>
              <i className="snap-key-band" aria-hidden="true" />
              usual range
            </li>
          ) : null}
          {target ? (
            <li>
              <i className="snap-key-target" aria-hidden="true" />
              target {target.label}
            </li>
          ) : null}
          {marks.length > 0 ? (
            <li>
              <i className="snap-key-mark" aria-hidden="true" />a day something else was unusual
            </li>
          ) : null}
        </ul>
        <span className="snap-chart-count">
          {reported} of the last {smooth.length} days reported.
        </span>
      </figcaption>
      {trend.run ? (
        <p className="snap-run" data-verdict={trend.run.verdict}>
          {trend.run.days} days in a row {snapshotVerdictPhrase(trend.run.verdict)}{" "}
          {midSentence(trend.label)}
        </p>
      ) : null}
      {/* The slower fact: whether the member's own usual has itself moved. */}
      {trend.drift ? (
        <p className="snap-drift">
          Usual {midSentence(trend.label)} is {trend.drift.now} · was {trend.drift.was} in{" "}
          {trend.drift.since}
        </p>
      ) : null}
    </figure>
  );
}
